/** Minimum shape for searchable elements (id, name, elementType) */
export interface SearchableElement {
	id: string;
	name: string;
	elementType: string;
}

export interface SearchKnowledgeElementsOptions {
	/** Graph ID - required for graph-scoped search (editing a specific graph) */
	graphId: string;
	/** Filter by element type: "nodes" (entities only, excludes Predicates), "predicates", or "all" */
	elementType?: "nodes" | "predicates" | "all";
	/** When true, empty query returns all elements (filtered by type). When false, empty query returns []. */
	includeAllWhenEmpty?: boolean;
}

/**
 * Filter knowledge elements by name (substring, case-insensitive).
 * Search is graph-scoped: the caller must pass elements relevant to the given graphId.
 * Used for editing-stage search: Add Nodes, Relation Target, Relation Type.
 *
 * @param elements - List of elements to search (graph-scoped by caller)
 * @param query - Search query (trimmed and lowercased internally)
 * @param options - Options including graphId, element type and empty-query behavior
 * @returns Filtered and sorted results (alphabetically by name)
 */
export function searchKnowledgeElements<T extends SearchableElement>(
	elements: T[],
	query: string,
	options: SearchKnowledgeElementsOptions,
): T[] {
	const { elementType = "all", includeAllWhenEmpty = false } = options;
	const trimmedQuery = query.trim().toLowerCase();

	// Filter by element type
	let filtered = elements;
	if (elementType === "nodes") {
		filtered = elements.filter((el) => el.elementType !== "Predicate");
	} else if (elementType === "predicates") {
		filtered = elements.filter((el) => el.elementType === "Predicate");
	}

	// Apply name filter
	if (trimmedQuery.length === 0) {
		return includeAllWhenEmpty ? filtered : [];
	}

	const byName = filtered.filter((el) => el.name.toLowerCase().includes(trimmedQuery));

	// Sort alphabetically (case-insensitive)
	return byName.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}
